import React, { useState, useEffect, useCallback } from 'react';

const WORKER_URL = 'https://drive-proxy.friedli-group.workers.dev';
const TASKS_WEBHOOK = 'https://n8n.friedli-group.com/webhook/tasks';

// "Classy, productive, definitive" palette — a deep navy/gold ledger feel
// instead of the earlier brighter SaaS purple/green mix. Key names are kept
// the same (purple/green/red/amber) so every existing dotColor/pill/BarChart
// reference below repaints automatically.
const COLORS = {
  purple: '#2F3B63', green: '#1E7A67', red: '#A94328',
  amber: '#96721E', gray: '#A9A69C', lightGray: '#F5F3EE',
  border: '#E6E2D8', text: '#1E1E22', subtext: '#7C7972',
};

// Soft pill tones — same visual language as the Emails tab's bucket labels
// (light tinted background, saturated text), reused everywhere as colored
// section headers instead of plain uppercase gray text.
const PILLS = {
  navy: { bg: '#E7E9F1', fg: '#2F3B63' },
  gold: { bg: '#F3EBD6', fg: '#8A6A1E' },
  teal: { bg: '#E1EFEA', fg: '#1E7A67' },
  rust: { bg: '#F7E5DE', fg: '#A94328' },
  slate: { bg: '#EBEAE5', fg: '#5B584F' },
};

// Colored pill-style section header — replaces plain uppercase gray labels
// throughout with the same soft-pill treatment as the Emails tab buckets.
const sectionHead = (label, tone = 'navy', icon, mb = 10) => (
  <div style={{ marginBottom: mb }}>
    <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20, background: PILLS[tone].bg, color: PILLS[tone].fg, letterSpacing: '0.02em' }}>
      {icon ? `${icon} ` : ''}{label}
    </span>
  </div>
);

const card = {
  background: '#fff', border: `0.5px solid ${COLORS.border}`,
  borderRadius: 14, padding: '1.1rem 1.15rem', marginBottom: 12,
  boxShadow: '0 1px 2px rgba(30,30,20,0.03), 0 1px 8px rgba(30,30,20,0.03)',
};

const pill = (color) => ({
  fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 20,
  background: color + '18', color, textTransform: 'uppercase', letterSpacing: '0.04em',
});

// Compact bordered row — the same visual language as the Emails tab's rows
// (individually rounded, tight padding, columns via flex) instead of one
// tall label-stacked box per item. Used for By Sport, Subscriptions,
// Notable Transactions, and similar short lists.
function RowList({ items, renderRow, gap = 7 }) {
  if (!items || items.length === 0) return null;
  return (
    <div>
      {items.map((item, i) => (
        <div key={i} style={{
          background: '#fff', border: `0.5px solid ${COLORS.border}`, borderRadius: 10,
          marginBottom: i < items.length - 1 ? gap : 0, padding: '9px 12px',
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          {renderRow(item, i)}
        </div>
      ))}
    </div>
  );
}

const dotColor = (status) => {
  if (status === 'active') return COLORS.green;
  if (status === 'error') return COLORS.red;
  if (status === 'pending') return COLORS.amber;
  return COLORS.gray;
};

const priorityColor = (p) => {
  if (p === 'high') return COLORS.red;
  if (p === 'medium') return COLORS.amber;
  return COLORS.gray;
};

// Formats a number as a plain CHF-style figure: no decimals, ' as thousands separator.
// e.g. 39028 -> "39'028", -154 -> "-154"
const formatNum = (n) => {
  const num = Math.round(Number(n) || 0);
  const sign = num < 0 ? '-' : '';
  const abs = Math.abs(num).toString().replace(/\B(?=(\d{3})+(?!\d))/g, "'");
  return sign + abs;
};
const formatCHF = (n) => `CHF ${formatNum(n)}`;

// Module-level date formatter (day + short month) for plain "YYYY-MM-DD" strings —
// used by components defined outside the App function that don't have access to
// the locale-aware formatters declared inside it.
const formatTripDateSafe = (iso) => {
  if (!iso) return '';
  try { return new Date(iso + 'T00:00:00').toLocaleDateString('de-CH', { day: 'numeric', month: 'short' }); }
  catch { return iso; }
};

// Simple single-hue horizontal bar chart — used for Finance's category/month breakdowns.
// No legend needed (single series ranking magnitude), direct value labels, thin rounded bars.
function BarChart({ items, labelKey, valueKey, formatValue = formatNum, color = COLORS.purple }) {
  if (!items || items.length === 0) return null;
  const max = Math.max(...items.map(i => Number(i[valueKey]) || 0), 1);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {items.map((item, i) => {
        const val = Number(item[valueKey]) || 0;
        const pct = Math.max((val / max) * 100, 2);
        return (
          <div key={i}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 3 }}>
              <span style={{ color: COLORS.text }}>{item[labelKey]}</span>
              <span style={{ color: COLORS.subtext, fontVariantNumeric: 'tabular-nums' }}>{formatValue(val)}</span>
            </div>
            <div style={{ background: COLORS.lightGray, borderRadius: 4, height: 8, overflow: 'hidden' }}>
              <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 4 }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Responsive multi-column grid — auto-fits as many columns as fit at minWidth,
// collapsing to a single column on mobile. Used throughout Finance/Health/
// Technology & Risk to condense information per Simon's "work with multiple
// columns" request instead of stacking everything vertically.
const grid = (minWidth = 300, gap = 12) => ({
  display: 'grid', gridTemplateColumns: `repeat(auto-fit, minmax(${minWidth}px, 1fr))`, gap,
});

// Small stat tile — used for at-a-glance numbers (Health running stats, etc.)
function StatTile({ label, value, sub, color }) {
  const c = color || COLORS.purple;
  return (
    <div style={{ background: c + '0d', borderLeft: `2.5px solid ${c}`, borderRadius: 8, padding: '0.65rem 0.8rem' }}>
      <div style={{ fontSize: 10, fontWeight: 600, color: COLORS.subtext, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 17, fontWeight: 600, color: c }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: COLORS.subtext, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

// Single-hue month × category spend heatmap — sequential color scale (per the
// dataviz guidance: single-hue for magnitude, no rainbow legend needed since
// darker = more). Replaces the old per-month bar-chart boxes so 8 months ×
// 13 categories fits without endless scrolling.
function SpendHeatmap({ matrix, formatValue = formatCHF }) {
  if (!matrix || !matrix.months || !matrix.categories) return null;
  const { months, categories, cells } = matrix;
  const lookup = {};
  let max = 1;
  (cells || []).forEach(c => {
    lookup[`${c.month}|${c.category}`] = c.amountCHF;
    if (c.amountCHF > max) max = c.amountCHF;
  });
  const monthLabel = (m) => {
    try { return new Date(m + '-01').toLocaleDateString('en-US', { month: 'short' }); } catch { return m; }
  };
  const cellColor = (v) => {
    if (!v) return 'transparent';
    const pct = Math.min(v / max, 1);
    const alpha = 0.08 + pct * 0.72;
    return `rgba(127, 119, 221, ${alpha})`; // COLORS.purple
  };
  return (
    <div style={{ overflowX: 'auto' }}>
      <div style={{ display: 'grid', gridTemplateColumns: `70px repeat(${categories.length}, minmax(58px, 1fr))`, gap: 3, minWidth: 70 + categories.length * 60 }}>
        <div />
        {categories.map(c => (
          <div key={c} title={c} style={{ fontSize: 9, fontWeight: 600, color: COLORS.subtext, writingMode: 'vertical-rl', transform: 'rotate(180deg)', textAlign: 'left', height: 70, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c}</div>
        ))}
        {months.map(m => (
          <React.Fragment key={m}>
            <div style={{ fontSize: 11, color: COLORS.subtext, display: 'flex', alignItems: 'center' }}>{monthLabel(m)}</div>
            {categories.map(c => {
              const v = lookup[`${m}|${c}`];
              return (
                <div key={c} title={`${c}, ${monthLabel(m)}: ${v ? formatValue(v) : '—'}`} style={{
                  background: cellColor(v), borderRadius: 4, height: 28,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 9, color: v && v / max > 0.55 ? '#fff' : COLORS.text,
                }}>{v ? formatNum(v) : ''}</div>
              );
            })}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

const segmentIcon = (type) => {
  if (type === 'flight') return '✈️';
  if (type === 'accommodation') return '🏨';
  if (type === 'car') return '🚗';
  if (type === 'activity') return '🎯';
  return '📍';
};

const BUCKET_CONFIG = [
  {
    key: 'Urgent',
    label: 'Urgent',
    emptyText: 'Nothing urgent right now.',
    pillBg: '#FCEBEB',
    pillColor: '#A32D2D',
    icon: '🚨',
  },
  {
    key: 'Action',
    label: 'Action needed',
    emptyText: 'No action items.',
    pillBg: '#FFF8E7',
    pillColor: '#7A5C00',
    icon: '⚡',
  },
  {
    key: 'FYI',
    label: 'FYI',
    emptyText: 'No FYI emails.',
    pillBg: '#E6F1FB',
    pillColor: '#0C447C',
    icon: 'ℹ️',
  },
  {
    key: 'Receipt',
    label: 'Receipts & Confirmations',
    emptyText: 'No receipts.',
    pillBg: '#F1EFE8',
    pillColor: '#444441',
    icon: '🧾',
  },
  {
    key: 'Newsletter/Ads',
    label: 'Newsletters & Ads',
    emptyText: 'No newsletters.',
    pillBg: '#F0F0F0',
    pillColor: '#666666',
    icon: '📰',
  },
];

// Match email to bucket — handles variants like "Newsletter", "Ads", "Newsletter/Ads"
const matchBucket = (email, bucketKey) => {
  const cat = (email.category || '').trim();
  if (bucketKey === 'Newsletter/Ads') {
    return cat === 'Newsletter/Ads' || cat === 'Newsletter' || cat === 'Ads' || cat === 'Informational';
  }
  return cat === bucketKey;
};

function EmailRow({ email, done, isExpanded, onToggleExpand, onToggleComplete }) {
  const formatEmailDate = (ts) => {
    if (!ts) return '';
    try {
      const d = new Date(ts);
      if (isNaN(d)) return '';
      const now = new Date();
      const diff = now - d;
      if (diff < 86400000) return d.toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' });
      if (diff < 604800000) return d.toLocaleDateString('de-CH', { weekday: 'short' });
      return d.toLocaleDateString('de-CH', { day: 'numeric', month: 'short' });
    } catch { return ''; }
  };

  const gmailUrl = email.gmailLink || `https://mail.google.com/mail/u/0/#inbox/${email.id}`;

  return (
    <div style={{
      background: '#fff',
      border: `0.5px solid ${COLORS.border}`,
      borderRadius: 10,
      marginBottom: 8,
      opacity: done ? 0.45 : 1,
      overflow: 'hidden',
    }}>
      {/* Row header */}
      <div
        onClick={onToggleExpand}
        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px', cursor: 'pointer' }}
      >
        <button
          onClick={e => { e.stopPropagation(); onToggleComplete(email.id); }}
          style={{
            width: 16, height: 16, borderRadius: 3,
            border: `1.5px solid ${done ? COLORS.green : COLORS.border}`,
            background: done ? COLORS.green : '#fff',
            cursor: 'pointer', flexShrink: 0, color: '#fff',
            fontSize: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >{done ? '✓' : ''}</button>
        <span style={{ fontSize: 12, color: COLORS.subtext, flexShrink: 0, minWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {(email.from || email.fromAddress || '').substring(0, 22)}
        </span>
        <span style={{
          fontSize: 13, flex: 1, fontWeight: done ? 400 : 500,
          textDecoration: done ? 'line-through' : 'none',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{email.subject || '(no subject)'}</span>
        <span style={{ fontSize: 11, color: COLORS.subtext, flexShrink: 0, marginRight: 4 }}>
          {formatEmailDate(email.date)}
        </span>
        <span style={{ fontSize: 11, color: COLORS.subtext }}>
          {isExpanded ? '▲' : '▼'}
        </span>
      </div>

      {/* Expanded body */}
      {isExpanded && !done && (
        <div style={{ borderTop: `0.5px solid ${COLORS.border}`, padding: '12px 14px 14px' }}>
          {email.snippet && (
            <div style={{ fontSize: 12, color: '#aaa', lineHeight: 1.5, marginBottom: 8, fontStyle: 'italic' }}>
              {email.snippet.substring(0, 200)}{email.snippet.length > 200 ? '…' : ''}
            </div>
          )}
          <div style={{ fontSize: 13, color: COLORS.subtext, lineHeight: 1.65, marginBottom: 10 }}>
            {email.summary}
          </div>
          {email.actionNeeded && (
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: '#FFF8E7', borderRadius: 6, padding: '5px 10px',
              fontSize: 12, color: '#7A5C00', marginBottom: 10,
            }}>
              <span>⚡</span><span>{email.actionNeeded}</span>
            </div>
          )}
          {email.suggestedReply && (
            <div style={{ background: COLORS.lightGray, borderRadius: 8, padding: '10px 12px', marginBottom: 10 }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: COLORS.subtext, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                💬 Draft reply
              </div>
              <div style={{ fontSize: 12, lineHeight: 1.7, whiteSpace: 'pre-line', color: COLORS.text }}>
                {email.suggestedReply}
              </div>
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
            <a
              href={gmailUrl}
              target="_blank"
              rel="noreferrer"
              style={{
                fontSize: 11, padding: '5px 12px', borderRadius: 6,
                border: 'none', background: COLORS.purple, color: '#fff',
                cursor: 'pointer', fontWeight: 500, textDecoration: 'none',
                display: 'inline-flex', alignItems: 'center', gap: 4,
              }}
            >↗ Open in Gmail</a>
            {email.suggestedReply && (
              <button
                onClick={() => navigator.clipboard?.writeText(email.suggestedReply)}
                style={{
                  fontSize: 11, padding: '5px 12px', borderRadius: 6,
                  border: `1px solid ${COLORS.border}`, background: '#fff',
                  cursor: 'pointer', color: COLORS.subtext,
                }}
              >Copy reply</button>
            )}
            <button
              onClick={() => onToggleComplete(email.id)}
              style={{
                fontSize: 11, padding: '5px 12px', borderRadius: 6,
                border: `1px solid ${COLORS.border}`, background: '#fff',
                cursor: 'pointer', color: COLORS.subtext,
              }}
            >✓ Mark done</button>
          </div>
        </div>
      )}
    </div>
  );
}

function EmailBucket({ bucket, emails, completedEmailIds, expandedEmailId, onToggleExpand, onToggleComplete }) {
  const pending = emails.filter(e => !completedEmailIds.includes(e.id));
  const completed = emails.filter(e => completedEmailIds.includes(e.id));
  const total = emails.length;

  if (total === 0) return null;

  return (
    <div style={{ marginBottom: '1.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <span style={{
          fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20,
          background: bucket.pillBg, color: bucket.pillColor,
        }}>{bucket.label}</span>
        <span style={{ fontSize: 12, color: COLORS.subtext }}>
          {total} email{total !== 1 ? 's' : ''}
          {completed.length > 0 ? ` · ${completed.length} done` : ''}
        </span>
      </div>

      {pending.length === 0 && completed.length > 0 && (
        <div style={{ fontSize: 12, color: COLORS.subtext, padding: '4px 2px', marginBottom: 6 }}>
          {bucket.emptyText}
        </div>
      )}
      {pending.map(email => (
        <EmailRow
          key={email.id}
          email={email}
          done={false}
          isExpanded={expandedEmailId === email.id}
          onToggleExpand={() => onToggleExpand(email.id)}
          onToggleComplete={onToggleComplete}
        />
      ))}

      {completed.length > 0 && (
        completed.map(email => (
          <EmailRow
            key={email.id}
            email={email}
            done={true}
            isExpanded={false}
            onToggleExpand={() => {}}
            onToggleComplete={onToggleComplete}
          />
        ))
      )}
    </div>
  );
}

const prettyLabel = (key) => key
  .replace(/([a-z])([A-Z])/g, '$1 $2')
  .replace(/^./, s => s.toUpperCase());

// Recursively renders an arbitrary JSON value (object/array/primitive) as readable
// cards/rows, so any chief's "data" payload — whatever shape it has — displays cleanly
// without needing bespoke UI per chief.
function ChiefDataValue({ value, depth = 0 }) {
  if (value === null || value === undefined || value === '') return null;

  if (Array.isArray(value)) {
    if (value.length === 0) return null;
    const primitives = value.every(v => typeof v !== 'object' || v === null);
    if (primitives) {
      return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {value.map((v, i) => (
            <span key={i} style={{ fontSize: 12, background: COLORS.lightGray, borderRadius: 20, padding: '3px 10px', color: COLORS.text }}>{String(v)}</span>
          ))}
        </div>
      );
    }
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {value.map((item, i) => (
          <div key={i} style={{ background: COLORS.lightGray, borderRadius: 10, padding: '0.75rem 1rem' }}>
            <ChiefDataValue value={item} depth={depth + 1} />
          </div>
        ))}
      </div>
    );
  }

  if (typeof value === 'object') {
    const entries = Object.entries(value).filter(([, v]) => v !== null && v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0));
    if (entries.length === 0) return null;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: depth === 0 ? 14 : 6 }}>
        {entries.map(([k, v]) => {
          const isSimple = typeof v !== 'object';
          return (
            <div key={k}>
              <div style={{ fontSize: depth === 0 ? 11 : 12, fontWeight: 600, color: depth === 0 ? COLORS.subtext : COLORS.text, textTransform: depth === 0 ? 'uppercase' : 'none', letterSpacing: depth === 0 ? '0.05em' : 'normal', marginBottom: 4 }}>
                {prettyLabel(k)}
              </div>
              {isSimple ? (
                <div style={{ fontSize: 13, color: COLORS.text, lineHeight: 1.6 }}>{String(v)}</div>
              ) : (
                <ChiefDataValue value={v} depth={depth + 1} />
              )}
            </div>
          );
        })}
      </div>
    );
  }

  return <div style={{ fontSize: 13, color: COLORS.text }}>{String(value)}</div>;
}

// Renders a single chief's own header (name, status) with all of their available
// detail/data below it — the building block used to give each domain tab (Finance,
// Health, Technology, Insurance, Learning, Risk, plus the chiefs folded into Brief
// and Emails) a real, self-contained overview instead of a link to click through.
function ChiefSection({ chief, heading }) {
  if (!chief) {
    return (
      <div style={{ ...card, fontSize: 13, color: COLORS.subtext }}>
        {heading ? `${heading}: ` : ''}No data yet.
      </div>
    );
  }
  const { name, status, detail, ...rest } = chief;
  const extra = { ...rest };
  delete extra.data;
  const dataPayload = chief.data || (Object.keys(extra).length ? extra : null);

  return (
    <div style={{ ...card, marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
        <span style={{ width: 9, height: 9, borderRadius: '50%', background: dotColor(status), display: 'inline-block' }} />
        <h3 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>{name}</h3>
        <span style={pill(dotColor(status))}>{status}</span>
      </div>
      {detail && (
        <p style={{ fontSize: 13, color: COLORS.subtext, lineHeight: 1.7, margin: '10px 0 14px' }}>{detail}</p>
      )}
      {dataPayload ? (
        <ChiefDataValue value={dataPayload} />
      ) : (
        <div style={{ fontSize: 13, color: COLORS.subtext }}>No additional detail yet.</div>
      )}
    </div>
  );
}

// Finance gets bespoke rendering (charts + CHF-formatted figures) instead of the
// generic ChiefDataValue recursion, since it's the one tab where magnitude and
// currency formatting genuinely matter for readability.
function FinanceSection({ chief }) {
  if (!chief) return <div style={{ ...card, fontSize: 13, color: COLORS.subtext }}>No data yet.</div>;
  const d = chief.data || {};
  const categories = [...(d.topCategories || [])].sort((a, b) => (b.totalCHF || 0) - (a.totalCHF || 0));

  return (
    <div>
      <div style={{ ...card, marginBottom: 16, display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <span style={{ width: 9, height: 9, borderRadius: '50%', background: dotColor(chief.status), display: 'inline-block' }} />
            <h3 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>{chief.name}</h3>
            <span style={pill(dotColor(chief.status))}>{chief.status}</span>
          </div>
          {chief.detail && <p style={{ fontSize: 13, color: COLORS.subtext, lineHeight: 1.7, margin: '10px 0 0', maxWidth: 480 }}>{chief.detail}</p>}
        </div>
        {d.totalSpendCHF != null && (
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 12, color: COLORS.subtext }}>Total spend, {d.coverage?.from} → {d.coverage?.through}</div>
            <div style={{ fontSize: 28, fontWeight: 600 }}>{formatCHF(d.totalSpendCHF)}</div>
          </div>
        )}
      </div>

      {/* Subscriptions + Notable transactions pinned at top, as email-style row lists */}
      <div style={{ ...grid(340), marginBottom: 16 }}>
        {(d.subscriptions || []).length > 0 && (
          <div style={{ ...card, marginBottom: 0 }}>
            {sectionHead('Subscriptions', 'gold', '🔁')}
            <RowList items={d.subscriptions} renderRow={(s) => (
              <>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: COLORS.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.name}</div>
                  <div style={{ fontSize: 11, color: COLORS.subtext, marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.frequency}{s.note ? ` — ${s.note}` : ''}</div>
                </div>
                <div style={{ fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap' }}>{formatCHF(s.amountCHF)}</div>
              </>
            )} />
          </div>
        )}
        {(d.notableTransactions || []).length > 0 && (
          <div style={{ ...card, marginBottom: 0 }}>
            {sectionHead('Notable transactions', 'slate', '🧾')}
            <RowList items={d.notableTransactions} renderRow={(t) => (
              <>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: COLORS.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.merchant}</div>
                  <div style={{ fontSize: 11, color: COLORS.subtext, marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{formatTripDateSafe(t.date)}{t.note ? ` — ${t.note}` : ''}</div>
                </div>
                <div style={{ fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap' }}>{formatCHF(t.amountCHF)}</div>
              </>
            )} />
          </div>
        )}
      </div>

      {/* Month × category heatmap — replaces per-month bar boxes */}
      {d.monthCategoryMatrix && (
        <div style={{ ...card, marginBottom: 16 }}>
          {sectionHead('Spend by month × category', 'navy', '📊', 14)}
          <SpendHeatmap matrix={d.monthCategoryMatrix} />
        </div>
      )}

      <div style={{ ...grid(340), marginBottom: 16 }}>
        {categories.length > 0 && (
          <div style={{ ...card, marginBottom: 0 }}>
            {sectionHead('Spend by category', 'teal', null, 14)}
            <BarChart items={categories} labelKey="category" valueKey="totalCHF" formatValue={formatCHF} color={COLORS.green} />
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {(d.moneySavingIdeas || []).length > 0 && (
            <div style={{ ...card, marginBottom: 0 }}>
              {sectionHead('Money-saving ideas', 'gold', '💡')}
              <ChiefDataValue value={d.moneySavingIdeas} />
            </div>
          )}
          {(d.dataQualityNotes || []).length > 0 && (
            <div style={{ ...card, marginBottom: 0 }}>
              {sectionHead('Notes on this analysis', 'slate')}
              <ChiefDataValue value={d.dataQualityNotes} />
            </div>
          )}
        </div>
      </div>

      {d.nextUpdate && <div style={{ fontSize: 12, color: COLORS.subtext, fontStyle: 'italic' }}>{d.nextUpdate}</div>}
    </div>
  );
}

// Insurance also gets a light custom wrapper so the analysis reliably renders
// above the individual policies, regardless of key order in the underlying data.
function InsuranceSection({ chief }) {
  if (!chief) return <div style={{ ...card, fontSize: 13, color: COLORS.subtext }}>No data yet.</div>;
  const d = chief.data || {};
  return (
    <div>
      <div style={{ ...card, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <span style={{ width: 9, height: 9, borderRadius: '50%', background: dotColor(chief.status), display: 'inline-block' }} />
          <h3 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>{chief.name}</h3>
          <span style={pill(dotColor(chief.status))}>{chief.status}</span>
        </div>
        {chief.detail && <p style={{ fontSize: 13, color: COLORS.subtext, lineHeight: 1.7, margin: '10px 0 0' }}>{chief.detail}</p>}
      </div>
      {d.analysis && (
        <div style={{ ...card, marginBottom: 16 }}>
          {sectionHead('Analysis', 'navy', '🛡️')}
          <ChiefDataValue value={d.analysis} />
        </div>
      )}
      {(d.policies || []).length > 0 && (
        <div style={{ ...card, marginBottom: 16 }}>
          {sectionHead('Policies', 'slate', '📄')}
          <RowList items={d.policies} renderRow={(p) => (
            <>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 500 }}>{p.provider} — {p.type}</div>
                <div style={{ fontSize: 11, color: COLORS.subtext, marginTop: 1 }}>{p.details}{p.validTo ? ` · valid to ${p.validTo}` : ''}</div>
              </div>
              <div style={{ fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap', textAlign: 'right' }}>
                {p.annualPremiumCHF != null ? `${formatCHF(p.annualPremiumCHF)}/yr` : (p.monthlyPremiumCHF != null ? `${formatCHF(p.monthlyPremiumCHF)}/mo` : '')}
              </div>
            </>
          )} />
        </div>
      )}
    </div>
  );
}

const MILESTONE_STATUS_DOT = { done: 'active', overdue: 'error', upcoming: 'pending' };

// Chief of Foundation — modeled on Finance's layout (stat header + BarChart)
// for the contributions-by-recipient breakdown, plus a milestone timeline and
// an "About" card pulled from the foundation's own site. New 12 Aug 2026.
function FoundationSection({ chief }) {
  if (!chief) return <div style={{ ...card, fontSize: 13, color: COLORS.subtext }}>No data yet.</div>;
  const d = chief.data || {};
  const contributions = [...(d.contributionsByRecipient || [])].sort((a, b) => (b.totalCHF || 0) - (a.totalCHF || 0));
  const milestones = [...(d.milestones || [])].sort((a, b) => (a.date || '').localeCompare(b.date || ''));

  return (
    <div>
      <div style={{ ...card, marginBottom: 16, display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <span style={{ width: 9, height: 9, borderRadius: '50%', background: dotColor(chief.status), display: 'inline-block' }} />
            <h3 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>{chief.name}</h3>
            <span style={pill(dotColor(chief.status))}>{chief.status}</span>
          </div>
          {chief.detail && <p style={{ fontSize: 13, color: COLORS.subtext, lineHeight: 1.7, margin: '10px 0 0', maxWidth: 480 }}>{chief.detail}</p>}
        </div>
        {d.totalContributedCHF != null && (
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 12, color: COLORS.subtext }}>Total contributed{d.contributionsCoverage ? `, ${d.contributionsCoverage}` : ''}</div>
            <div style={{ fontSize: 28, fontWeight: 600 }}>{formatCHF(d.totalContributedCHF)}</div>
          </div>
        )}
      </div>

      {d.journalEntries && (
        <div style={{ ...card, marginBottom: 16, borderLeft: `3px solid ${COLORS.gray}` }}>
          {sectionHead('Journal entries', 'slate', '🗂️')}
          <div style={{ fontSize: 13, lineHeight: 1.6 }}>{d.journalEntries.note}</div>
          <div style={{ fontSize: 11, color: COLORS.subtext, marginTop: 6 }}>
            {d.journalEntries.entriesLogged != null ? `${d.journalEntries.entriesLogged} entries logged so far` : ''}
            {d.journalEntries.lastEntryDate ? ` · last entry ${d.journalEntries.lastEntryDate}` : ''}
          </div>
        </div>
      )}

      {d.about && (
        <div style={{ ...card, marginBottom: 16 }}>
          {sectionHead('About', 'navy', '🌱')}
          {d.about.mission && <div style={{ fontSize: 13, lineHeight: 1.7 }}>{d.about.mission}</div>}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 10, fontSize: 12, color: COLORS.subtext }}>
            {d.about.founders && <span>👥 {d.about.founders}</span>}
            {d.about.focus && <span>🎯 {d.about.focus}</span>}
          </div>
          {d.about.source && <div style={{ fontSize: 11, color: '#ccc', marginTop: 8 }}>Source: {d.about.source}{d.about.lastChecked ? ` · checked ${d.about.lastChecked}` : ''}</div>}
        </div>
      )}

      <div style={{ ...grid(340), marginBottom: 16 }}>
        {contributions.length > 0 && (
          <div style={{ ...card, marginBottom: 0 }}>
            {sectionHead('Contributions by recipient', 'teal', '📊')}
            <BarChart items={contributions} labelKey="recipient" valueKey="totalCHF" formatValue={formatCHF} color={COLORS.green} />
          </div>
        )}
        {milestones.length > 0 && (
          <div style={{ ...card, marginBottom: 0 }}>
            {sectionHead('Milestones & submissions', 'gold', '📅')}
            <RowList items={milestones} renderRow={(m) => (
              <>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: dotColor(MILESTONE_STATUS_DOT[m.status] || 'default'), display: 'inline-block', flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{m.title}</div>
                  {m.note && <div style={{ fontSize: 11, color: COLORS.subtext, marginTop: 1 }}>{m.note}</div>}
                </div>
                <div style={{ fontSize: 11, color: COLORS.subtext, whiteSpace: 'nowrap' }}>{formatTripDateSafe(m.date)}</div>
              </>
            )} />
          </div>
        )}
      </div>

      {(d.recipients || []).length > 0 && (
        <div style={{ ...card, marginBottom: 16 }}>
          {sectionHead('Organizations we support', 'slate', '🏛️')}
          <RowList items={d.recipients} renderRow={(r) => (
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <div style={{ fontSize: 13, fontWeight: 500 }}>{r.name}</div>
                {r.website && <a href={r.website} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: COLORS.purple, textDecoration: 'none' }}>↗</a>}
              </div>
              {r.description && <div style={{ fontSize: 12, color: COLORS.subtext, marginTop: 2, lineHeight: 1.5 }}>{r.description}</div>}
            </div>
          )} />
        </div>
      )}

      {d.lastReviewed && <div style={{ fontSize: 11, color: '#ccc' }}>Last reviewed: {d.lastReviewed}</div>}
    </div>
  );
}

const SPORT_ICON = {
  Running: '🏃', HIIT: '🔥', Cycling: '🚴', Hiking: '🥾',
  'Weight training': '🏋️', Badminton: '🏸', 'Alpine skiing': '⛷️',
};

// Health gets a bespoke condensed layout: key stat tiles up top, then a
// multi-column grid for the sport breakdown, running detail, and Hyrox pass —
// instead of one long stacked recursive render.
function HealthSection({ chief }) {
  if (!chief) return <div style={{ ...card, fontSize: 13, color: COLORS.subtext }}>No data yet.</div>;
  const d = chief.data || {};
  const fo = d.fitnessOverview || {};
  const running = fo.running || {};
  const bySport = fo.bySport || [];

  return (
    <div>
      <div style={{ ...card, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <span style={{ width: 9, height: 9, borderRadius: '50%', background: dotColor(chief.status), display: 'inline-block' }} />
          <h3 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>{chief.name}</h3>
          <span style={pill(dotColor(chief.status))}>{chief.status}</span>
        </div>
        {chief.detail && <p style={{ fontSize: 13, color: COLORS.subtext, lineHeight: 1.7, margin: '10px 0 0' }}>{chief.detail}</p>}
        {d.program && (
          <div style={{ marginTop: 10, fontSize: 12, color: COLORS.text, background: COLORS.lightGray, borderRadius: 8, padding: '8px 12px', display: 'inline-block' }}>
            🎯 {d.program}
          </div>
        )}
      </div>

      {/* Insights — coaching-style takeaways on training load, nutrition,
          recovery and event ideas, derived from the Strava data below. Sits
          right under the header per Simon's request so it's the first thing
          he sees. Refreshed weekly. */}
      {d.insights?.items?.length > 0 && (
        <div style={{ ...card, marginBottom: 16 }}>
          {sectionHead('Insights', 'gold', '💡')}
          {d.insights.summary && <div style={{ fontSize: 12, color: COLORS.subtext, marginBottom: 10, lineHeight: 1.5 }}>{d.insights.summary}</div>}
          {d.insights.items.map((it, i) => (
            <div key={i} style={{ padding: '8px 0', borderBottom: i < d.insights.items.length - 1 ? `0.5px solid ${COLORS.border}` : 'none' }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{it.icon ? `${it.icon} ` : ''}{it.title}</div>
              <div style={{ fontSize: 12, color: COLORS.subtext, marginTop: 3, lineHeight: 1.5 }}>{it.tip}</div>
            </div>
          ))}
        </div>
      )}

      {/* Running stat tiles */}
      {(running.avgPacePerKm || running.best5k) && (
        <div style={{ ...grid(150, 10), marginBottom: 16 }}>
          <StatTile label="Avg pace" value={running.avgPacePerKm} color={COLORS.purple} />
          <StatTile label="Best 5k (est.)" value={running.best5k} color={COLORS.green} />
          <StatTile label="Longest run" value={running.longestRun?.split(' on ')[0]} sub={running.longestRun?.split(', ')[1]} color={COLORS.amber} />
          <StatTile label="Most recent" value={running.recentRun?.split(' on ')[0]?.replace('km', 'km')} sub={running.recentRun} color={COLORS.red} />
        </div>
      )}

      <div style={{ ...grid(320), marginBottom: 16 }}>
        {/* By-sport — email-row style list instead of a label-stacked box per sport */}
        {bySport.length > 0 && (
          <div style={{ ...card, marginBottom: 0 }}>
            {sectionHead(`By sport${fo.windowCovered ? ` — ${fo.windowCovered}` : ''}`, 'teal', '🏃')}
            {fo.activityFrequency && <div style={{ fontSize: 12, color: COLORS.subtext, marginBottom: 8, lineHeight: 1.5 }}>{fo.activityFrequency}</div>}
            <RowList gap={6} items={bySport} renderRow={(s) => (
              <>
                <span style={{ fontSize: 13, flex: 1 }}>{SPORT_ICON[s.sport] || '•'} {s.sport}</span>
                <span style={{ fontSize: 12, color: COLORS.subtext, minWidth: 66, textAlign: 'right' }}>{s.sessions} session{s.sessions !== 1 ? 's' : ''}</span>
                <span style={{ fontSize: 12, color: COLORS.subtext, minWidth: 70, textAlign: 'right' }}>{s.totalDistanceKm ? `${s.totalDistanceKm}km` : ''}{s.totalDistanceKm && s.totalHours ? ' · ' : ''}{s.totalHours ? `${s.totalHours}h` : ''}</span>
              </>
            )} />
          </div>
        )}

        {/* Training + Hyrox combined into one tighter card */}
        <div style={{ ...card, marginBottom: 0 }}>
          {(d.training || []).length > 0 && (
            <>
              {sectionHead('Training routine', 'navy', '📋')}
              <ChiefDataValue value={d.training} />
              {d.nextSession && <div style={{ fontSize: 12, color: COLORS.purple, marginTop: 8, fontWeight: 500 }}>▶ Next: {d.nextSession}</div>}
            </>
          )}
          {d.hyrox && (
            <div style={{ marginTop: (d.training || []).length > 0 ? 14 : 0, paddingTop: (d.training || []).length > 0 ? 12 : 0, borderTop: (d.training || []).length > 0 ? `0.5px solid ${COLORS.border}` : 'none' }}>
              {sectionHead(`Hyrox — ${d.hyrox.studio}`, 'gold', '🥊')}
              <div style={{ fontSize: 13 }}>{d.hyrox.currentPass}</div>
              {d.hyrox.note && <div style={{ fontSize: 12, color: COLORS.subtext, marginTop: 4 }}>{d.hyrox.note}</div>}
            </div>
          )}
        </div>
      </div>

      {fo.note && (
        <div style={{ ...card, marginBottom: 16, borderLeft: `3px solid ${COLORS.green}` }}>
          <div style={{ fontSize: 13, lineHeight: 1.6 }}>{fo.note}</div>
          {fo.heartRateZones && <div style={{ fontSize: 12, color: COLORS.subtext, marginTop: 6 }}>❤️ {fo.heartRateZones}</div>}
        </div>
      )}

      {d.lastReviewed && <div style={{ fontSize: 11, color: '#ccc' }}>Last reviewed: {d.lastReviewed}</div>}
    </div>
  );
}

// Technology & Risk get a shared condensed layout: Technology's system status
// as one tile, Risk's 4 active jobs as compact tiles in the same grid.
function TechRiskSection({ tech, risk }) {
  const jobs = risk?.data?.activeJobs || [];
  const systems = tech?.data?.systems || [];

  const jobIcon = {
    'Insurance coverage & renewal watchdog': '🛡️',
    'Large / unusual transaction alerts': '🚨',
    'Pre-trip risk briefings': '✈️',
    'Subscription & renewal tracker': '🔁',
  };

  const renderJobBody = (job) => {
    const jd = job.data || {};
    if (job.job.includes('Insurance')) {
      return (
        <>
          <div style={{ fontSize: 12, color: COLORS.subtext }}>{jd.policiesTracked} policies tracked</div>
          {jd.nextRenewal && <div style={{ fontSize: 12, marginTop: 4 }}>Next: {jd.nextRenewal.policy}</div>}
          {jd.flaggedOverlap && <div style={{ fontSize: 12, color: COLORS.subtext, marginTop: 6, lineHeight: 1.5 }}>⚠ Overlap: {jd.flaggedOverlap}</div>}
          {jd.flaggedGaps?.length > 0 && (
            <div style={{ marginTop: 6 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.amber, marginBottom: 3 }}>{jd.flaggedGaps.length} gap{jd.flaggedGaps.length !== 1 ? 's' : ''} flagged:</div>
              {jd.flaggedGaps.map((g, gi) => (
                <div key={gi} style={{ fontSize: 12, color: COLORS.text, lineHeight: 1.5, padding: '3px 0' }}>• {g}</div>
              ))}
            </div>
          )}
        </>
      );
    }
    if (job.job.includes('transaction')) {
      return (
        <>
          <div style={{ fontSize: 12, color: COLORS.subtext }}>Threshold: {formatCHF(jd.thresholdCHF)}</div>
          {jd.flagged?.length > 0 ? (
            <div style={{ marginTop: 6 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.text, marginBottom: 3 }}>{jd.flagged.length} flagged this period:</div>
              {jd.flagged.map((f, fi) => (
                <div key={fi} style={{ fontSize: 12, lineHeight: 1.5, padding: '3px 0', borderBottom: fi < jd.flagged.length - 1 ? `0.5px solid ${COLORS.border}` : 'none' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>{f.merchant}</span>
                    <span style={{ fontWeight: 500 }}>{formatCHF(f.amountCHF)}</span>
                  </div>
                  <div style={{ fontSize: 11, color: COLORS.subtext, marginTop: 1 }}>{formatTripDateSafe(f.date)}{f.verdict ? ` — ${f.verdict}` : ''}</div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ fontSize: 12, marginTop: 4, color: COLORS.subtext }}>Nothing flagged this period.</div>
          )}
        </>
      );
    }
    if (job.job.includes('Pre-trip')) {
      return <div style={{ fontSize: 12, color: COLORS.subtext }}>{jd.briefings?.length || 0} trips briefed — see Travel tab</div>;
    }
    if (job.job.includes('Subscription')) {
      const flagged = (jd.subscriptions || []).filter(s => s.flag);
      return (
        <>
          <div style={{ fontSize: 12, color: COLORS.subtext }}>{jd.subscriptions?.length || 0} tracked{flagged.length ? `, ${flagged.length} needs a decision` : ''}</div>
          {flagged.map((s, i) => <div key={i} style={{ fontSize: 12, color: COLORS.amber, marginTop: 4 }}>⚠ {s.name} — {s.cadence}</div>)}
        </>
      );
    }
    return <ChiefDataValue value={jd} depth={1} />;
  };

  return (
    <div>
      <div style={{ ...grid(280), marginBottom: 16 }}>
        {tech && (
          <div style={{ ...card, marginBottom: 0, borderLeft: `3px solid ${dotColor(tech.status)}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <span style={{ fontSize: 13, fontWeight: 600 }}>💻 {tech.name}</span>
              <span style={pill(dotColor(tech.status))}>{tech.status}</span>
            </div>
            {tech.detail && <div style={{ fontSize: 12, color: COLORS.subtext, marginTop: 6, lineHeight: 1.6 }}>{tech.detail}</div>}
            {systems.map((s, i) => (
              <div key={i} style={{ fontSize: 12, marginTop: 8, padding: '6px 8px', background: COLORS.lightGray, borderRadius: 6 }}>
                <strong>{s.name}</strong> — {s.status}
              </div>
            ))}
          </div>
        )}
        {jobs.map((job, i) => (
          <div key={i} style={{ ...card, marginBottom: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <span style={{ fontSize: 13, fontWeight: 600 }}>{jobIcon[job.job] || '⚙️'} {job.job}</span>
            </div>
            {renderJobBody(job)}
          </div>
        ))}
      </div>
      {risk?.data?.notYetActive?.length > 0 && (
        <div style={{ fontSize: 12, color: COLORS.subtext, fontStyle: 'italic' }}>Not yet active: {risk.data.notYetActive.join(' ')}</div>
      )}
    </div>
  );
}

// Learning tab — renders the IMD EMBA progress plus the four implemented
// content categories (Sports, Astronomy, Technology, Banking & Finance) as a
// condensed multi-column grid of themed cards.
function LearningSection({ chief }) {
  if (!chief) return <div style={{ ...card, fontSize: 13, color: COLORS.subtext }}>No data yet.</div>;
  const d = chief.data || {};
  const feed = d.learningFeed || {};
  const s = feed.sports, a = feed.astronomy, t = feed.technology, b = feed.bankingFinance;

  return (
    <div>
      <div style={{ ...card, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <span style={{ width: 9, height: 9, borderRadius: '50%', background: dotColor(chief.status), display: 'inline-block' }} />
          <h3 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>{chief.name}</h3>
          <span style={pill(dotColor(chief.status))}>{chief.status}</span>
        </div>
        {chief.detail && <p style={{ fontSize: 13, color: COLORS.subtext, lineHeight: 1.7, margin: '10px 0 0' }}>{chief.detail}</p>}
        {(d.milestones || []).length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
            {d.milestones.map((m, i) => <span key={i} style={{ fontSize: 11, background: COLORS.lightGray, borderRadius: 20, padding: '3px 10px' }}>{m}</span>)}
          </div>
        )}
        {d.upcoming && <div style={{ fontSize: 12, color: COLORS.purple, marginTop: 8 }}>▶ {d.upcoming}</div>}
      </div>

      <div style={{ ...grid(320) }}>
        {s && (
          <div style={{ ...card, marginBottom: 0 }}>
            {sectionHead('Sports', 'teal', '🏒')}
            {s.zscLions && (
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 500 }}>ZSC Lions 2026/27</div>
                <div style={{ fontSize: 12, color: COLORS.subtext, marginTop: 3, lineHeight: 1.6 }}>Season starts {s.zscLions.seasonStart}. {s.zscLions.opener}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
                  {s.zscLions.notableFixtures?.map((f, i) => <span key={i} style={{ fontSize: 10, background: COLORS.lightGray, borderRadius: 20, padding: '2px 8px' }}>{f}</span>)}
                </div>
              </div>
            )}
            {s.runningScience && (
              <div>
                <div style={{ fontSize: 12, fontWeight: 500 }}>🏃 {s.runningScience.topic}</div>
                {s.runningScience.notes?.map((n, i) => <div key={i} style={{ fontSize: 12, color: COLORS.subtext, marginTop: 4, lineHeight: 1.6 }}>• {n}</div>)}
              </div>
            )}
          </div>
        )}

        {a && (
          <div style={{ ...card, marginBottom: 0 }}>
            {sectionHead('Astronomy', 'navy', '🔭')}
            {a.thisWeek && <div style={{ fontSize: 12, lineHeight: 1.6, marginBottom: 10, background: COLORS.lightGray, borderRadius: 8, padding: '8px 10px' }}>✨ {a.thisWeek}</div>}
            {a.recentDiscoveries?.map((n, i) => <div key={i} style={{ fontSize: 12, color: COLORS.subtext, marginTop: 4, lineHeight: 1.6 }}>• {n}</div>)}
          </div>
        )}

        {t && (
          <div style={{ ...card, marginBottom: 0 }}>
            {sectionHead('Technology', 'slate', '💻')}
            {t.weeklyDigest && (
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 11, fontWeight: 500, color: COLORS.subtext, textTransform: 'uppercase' }}>Digest — {t.weeklyDigest.week}</div>
                {t.weeklyDigest.items?.map((n, i) => <div key={i} style={{ fontSize: 12, color: COLORS.subtext, marginTop: 4, lineHeight: 1.6 }}>• {n}</div>)}
              </div>
            )}
            {t.homeIdeasWithClaude && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 500, color: COLORS.subtext, textTransform: 'uppercase' }}>🏠 Home ideas with Claude</div>
                {t.homeIdeasWithClaude.map((n, i) => <div key={i} style={{ fontSize: 12, color: COLORS.subtext, marginTop: 4, lineHeight: 1.6 }}>• {n}</div>)}
              </div>
            )}
          </div>
        )}

        {b && (
          <div style={{ ...card, marginBottom: 0 }}>
            {sectionHead('Banking & Finance', 'gold', '🏦')}
            {b.weeklyMarketsBriefing && (
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 11, fontWeight: 500, color: COLORS.subtext, textTransform: 'uppercase' }}>Markets — {b.weeklyMarketsBriefing.week}</div>
                <div style={{ fontSize: 12, marginTop: 4, lineHeight: 1.6 }}>{b.weeklyMarketsBriefing.indices}</div>
                <div style={{ fontSize: 12, color: COLORS.subtext, marginTop: 4, lineHeight: 1.6 }}>{b.weeklyMarketsBriefing.drivers}</div>
              </div>
            )}
            {b.centralBankWatch && (
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 11, fontWeight: 500, color: COLORS.subtext, textTransform: 'uppercase' }}>Central banks</div>
                <div style={{ fontSize: 12, marginTop: 4, lineHeight: 1.6 }}>{b.centralBankWatch.latest}</div>
              </div>
            )}
            {b.readingList && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 500, color: COLORS.subtext, textTransform: 'uppercase' }}>📚 Reading list</div>
                {b.readingList.map((n, i) => <div key={i} style={{ fontSize: 12, color: COLORS.subtext, marginTop: 4, lineHeight: 1.6 }}>• {n}</div>)}
              </div>
            )}
          </div>
        )}
      </div>
      {d.lastReviewed && <div style={{ fontSize: 11, color: '#ccc', marginTop: 12 }}>Last reviewed: {d.lastReviewed}</div>}
    </div>
  );
}

const READING_BUCKET_ICON = { books: '📚', podcasts: '🎧', articles: '📰' };

function ReadingItemRow({ item }) {
  return (
    <div style={{ padding: '10px 0', borderBottom: `0.5px solid ${COLORS.border}` }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 600 }}>{item.title}</div>
        {item.link && (
          <a href={item.link} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: COLORS.purple, textDecoration: 'none', flexShrink: 0 }}>↗</a>
        )}
      </div>
      {item.creator && <div style={{ fontSize: 11, color: COLORS.subtext, marginTop: 1 }}>{item.creator}</div>}
      {item.summary && <div style={{ fontSize: 12, color: COLORS.text, marginTop: 5, lineHeight: 1.6 }}>{item.summary}</div>}
      {item.keyTakeaway && (
        <div style={{ fontSize: 12, color: COLORS.text, marginTop: 6, background: COLORS.lightGray, borderRadius: 8, padding: '6px 10px', lineHeight: 1.5 }}>
          💡 <strong>Key takeaway:</strong> {item.keyTakeaway}
        </div>
      )}
    </div>
  );
}

// Chief of Reading — three buckets (books/podcasts/articles), each item with
// a short summary and a highlighted key takeaway, modeled loosely on Chief of
// Life's category cards. New 12 Aug 2026, refreshes weekly (Saturdays).
function ReadingSection({ chief }) {
  if (!chief) return <div style={{ ...card, fontSize: 13, color: COLORS.subtext }}>No data yet.</div>;
  const d = chief.data || {};
  const buckets = [
    { key: 'books', label: 'Books', items: d.books || [] },
    { key: 'podcasts', label: 'Podcasts', items: d.podcasts || [] },
    { key: 'articles', label: 'Articles', items: d.articles || [] },
  ].filter(b => b.items.length > 0);

  return (
    <div>
      <div style={{ ...card, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <span style={{ width: 9, height: 9, borderRadius: '50%', background: dotColor(chief.status), display: 'inline-block' }} />
          <h3 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>{chief.name}</h3>
          <span style={pill(dotColor(chief.status))}>{chief.status}</span>
        </div>
        {chief.detail && <p style={{ fontSize: 13, color: COLORS.subtext, lineHeight: 1.7, margin: '10px 0 0' }}>{chief.detail}</p>}
      </div>

      <div style={grid(320)}>
        {buckets.map((b, bi) => (
          <div key={bi} style={{ ...card, marginBottom: 0 }}>
            {sectionHead(b.label, 'gold', READING_BUCKET_ICON[b.key] || '•')}
            {b.items.map((item, i) => <ReadingItemRow key={i} item={item} />)}
          </div>
        ))}
      </div>
      {d.lastReviewed && <div style={{ fontSize: 11, color: '#ccc', marginTop: 12 }}>Last reviewed: {d.lastReviewed}</div>}
    </div>
  );
}

// Chief of Life — personal admin (subscriptions/household) up top, then the
// Zürich Brief: a newsletter-style rundown of what's on in the city, grouped
// by category (Sports gets its own section per Simon's request — ZSC games,
// running, marathons — alongside the usual culture/nightlife/food picks),
// modeled on the reference "Zürich Brief" email format.
const LIFE_CATEGORY_ICON = {
  sportTeams: '⚽', sports: '🏒', events: '🗓️', concerts: '🎤', culture: '🖼️', nightlife: '🪩', eatDrink: '🍽️', comingUp: '📌',
};

function LifeEventRow({ ev }) {
  const meta = [ev.date, ev.venue, ev.price].filter(Boolean).join(' · ');
  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 500 }}>{ev.title}</div>
        {ev.link && (
          <a href={ev.link} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: COLORS.purple, whiteSpace: 'nowrap', textDecoration: 'none', flexShrink: 0 }}>↗</a>
        )}
      </div>
      {ev.description && <div style={{ fontSize: 12, color: COLORS.subtext, marginTop: 2, lineHeight: 1.5 }}>{ev.description}</div>}
      {meta && <div style={{ fontSize: 11, color: COLORS.subtext, marginTop: 4 }}>{meta}</div>}
    </div>
  );
}

function ChiefOfLifeSection({ chief }) {
  if (!chief) return <div style={{ ...card, fontSize: 13, color: COLORS.subtext }}>No data yet.</div>;
  const d = chief.data || {};
  const zb = d.zurichBrief;
  const categories = (zb?.categories || []).filter(c => (c.items || []).length > 0);

  return (
    <div>
      <div style={{ ...card, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <span style={{ width: 9, height: 9, borderRadius: '50%', background: dotColor(chief.status), display: 'inline-block' }} />
          <h3 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>{chief.name}</h3>
          <span style={pill(dotColor(chief.status))}>{chief.status}</span>
        </div>
        {chief.detail && <p style={{ fontSize: 13, color: COLORS.subtext, lineHeight: 1.7, margin: '10px 0 0' }}>{chief.detail}</p>}
      </div>

      {/* Personal admin — subscriptions & household, as before */}
      {((d.subscriptions || []).length > 0 || (d.household || []).length > 0) && (
        <div style={{ ...grid(300), marginBottom: 16 }}>
          {(d.subscriptions || []).length > 0 && (
            <div style={{ ...card, marginBottom: 0 }}>
              {sectionHead('Subscriptions', 'gold', '🔁')}
              <RowList items={d.subscriptions} renderRow={(s) => (
                <>
                  <span style={{ fontSize: 13, flex: 1 }}>{s.name}</span>
                  <span style={pill(dotColor(s.status?.toLowerCase().includes('renew') || s.status?.toLowerCase().includes('schedule') ? 'active' : 'default'))}>{s.status}</span>
                </>
              )} />
            </div>
          )}
          {(d.household || []).length > 0 && (
            <div style={{ ...card, marginBottom: 0 }}>
              {sectionHead('Household', 'slate', '🏠')}
              <RowList items={d.household} renderRow={(h) => (
                <>
                  <span style={{ fontSize: 13, flex: 1 }}>{h.name}</span>
                  <span style={pill(dotColor(h.status?.toLowerCase().includes('schedule') ? 'active' : 'default'))}>{h.status}</span>
                </>
              )} />
            </div>
          )}
        </div>
      )}

      {/* Something for you & Lou — 5 weekly date ideas with a calendar-based
          day recommendation. New 12 Aug 2026, refreshes with the weekly Life
          & Learning run. */}
      {(d.dateIdeas?.ideas || []).length > 0 && (
        <div style={{ ...card, marginBottom: 16 }}>
          {sectionHead(`Something for you & Lou${d.dateIdeas.weekLabel ? ` — ${d.dateIdeas.weekLabel}` : ''}`, 'rust', '💕', 10)}
          <RowList gap={8} items={d.dateIdeas.ideas} renderRow={(idea) => (
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{idea.title}</div>
                {idea.type && <span style={pill(COLORS.purple)}>{idea.type}</span>}
              </div>
              {idea.description && <div style={{ fontSize: 12, color: COLORS.text, marginTop: 4, lineHeight: 1.5 }}>{idea.description}</div>}
              {(idea.suggestedDay || idea.why) && (
                <div style={{ fontSize: 11, color: COLORS.subtext, marginTop: 4 }}>
                  {idea.suggestedDay && <>📅 {idea.suggestedDay}</>}
                  {idea.suggestedDay && idea.why ? ' — ' : ''}
                  {idea.why}
                </div>
              )}
            </div>
          )} />
          {d.dateIdeas.generatedOn && <div style={{ fontSize: 11, color: '#ccc', marginTop: 12 }}>Generated: {d.dateIdeas.generatedOn}</div>}
        </div>
      )}

      {/* Zürich Brief */}
      {zb && (
        <div style={{ ...card, marginBottom: 16 }}>
          {sectionHead(`Zürich Brief${zb.weekLabel ? ` — ${zb.weekLabel}` : ''}`, 'navy', '🏙️', 10)}
          {zb.intro && <p style={{ fontSize: 13, color: COLORS.text, lineHeight: 1.7, margin: '0 0 14px' }}>{zb.intro}</p>}

          {(zb.newSinceLastUpdate || []).length > 0 && (
            <div style={{ marginBottom: 16 }}>
              {sectionHead('New since last update', 'rust', '✨', 8)}
              {zb.newSinceLastUpdate.map((n, i) => (
                <div key={i} style={{ fontSize: 12, color: COLORS.text, lineHeight: 1.6, padding: '4px 0' }}>▸ {n.title || n}{n.description ? ` — ${n.description}` : ''}</div>
              ))}
            </div>
          )}

          <div style={grid(320)}>
            {categories.map((cat, ci) => (
              <div key={ci} style={{ ...card, marginBottom: 0 }}>
                {sectionHead(cat.label, (cat.key === 'sports' || cat.key === 'sportTeams') ? 'teal' : (cat.key === 'comingUp' ? 'gold' : cat.key === 'concerts' ? 'rust' : 'slate'), LIFE_CATEGORY_ICON[cat.key] || '•')}
                <RowList items={cat.items} renderRow={(ev) => <LifeEventRow ev={ev} />} />
              </div>
            ))}
          </div>
          {zb.lastGenerated && <div style={{ fontSize: 11, color: '#ccc', marginTop: 14 }}>Last generated: {zb.lastGenerated}</div>}
        </div>
      )}

      {d.lastReviewed && <div style={{ fontSize: 11, color: '#ccc' }}>Last reviewed: {d.lastReviewed}</div>}
    </div>
  );
}

export default function App() {
  const [activeTab, setActiveTab] = useState('brief');
  const [time, setTime] = useState(new Date());
  const [data, setData] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [trips, setTrips] = useState([]);
  const [emails, setEmails] = useState([]);
  const [newTaskText, setNewTaskText] = useState('');
  const [newTaskPriority, setNewTaskPriority] = useState('medium');
  const [loading, setLoading] = useState(true);
  const [taskSaving, setTaskSaving] = useState(false);
  const [lastFetch, setLastFetch] = useState(null);
  const [completedEmailIds, setCompletedEmailIds] = useState([]);
  const [expandedEmailId, setExpandedEmailId] = useState(null);
  const [emailFilter, setEmailFilter] = useState('all');

  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const loadData = useCallback(async () => {
    try {
      const [dashRes, taskRes, tripRes, emailRes] = await Promise.all([
        fetch(WORKER_URL + '?t=' + Date.now()),
        fetch(WORKER_URL + '/tasks?t=' + Date.now()),
        fetch(WORKER_URL + '/trips?t=' + Date.now()),
        fetch(WORKER_URL + '/emails?t=' + Date.now())
      ]);
      const dash = await dashRes.json();
      const taskData = await taskRes.json();
      const tripData = await tripRes.json();
      const emailData = await emailRes.json();
      setData(dash);
      setTasks(taskData?.tasks || []);
      setTrips(tripData?.trips || []);
      setEmails(emailData?.emails || []);
      setLastFetch(new Date());
    } catch(e) { console.error('Load error:', e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 60000);
    return () => clearInterval(interval);
  }, [loadData]);

  const taskAction = async (action, payload) => {
    setTaskSaving(true);
    try {
      const res = await fetch(TASKS_WEBHOOK, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, ...payload })
      });
      const result = await res.json();
      if (result.tasks) setTasks(result.tasks);
    } catch(e) { console.error('Task error:', e); }
    finally { setTaskSaving(false); }
  };

  const addTask = () => {
    if (!newTaskText.trim()) return;
    taskAction('add', { text: newTaskText.trim(), priority: newTaskPriority });
    setNewTaskText('');
  };

  const toggleTask = (id) => taskAction('toggle', { id });
  const deleteTask = (id) => taskAction('delete', { id });

  const toggleEmailComplete = (id) => {
    setCompletedEmailIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const toggleEmailExpand = (id) => {
    setExpandedEmailId(prev => prev === id ? null : id);
  };

  const brief = data?.brief || {};
  const chiefs = data?.chiefs || [];
  const findChief = (namePart) => chiefs.find(c => (c.name || '').toLowerCase().includes(namePart.toLowerCase()));
  const apiSpend = data?.apiSpend || { current: 0, budget: 60 };
  const currentMonthLabel = new Date().toLocaleDateString('en-US', { month: 'long' });
  const pendingTasks = tasks.filter(t => !t.done).length;
  const staffChiefData = findChief('Chief of Staff')?.data;
  const staffPriorities = staffChiefData?.priorities || [];

  // Include all emails except Travel (Travel goes to Travel tab)
  const activeEmails = emails.filter(e => e.category !== 'Travel');
  const urgentEmails = activeEmails.filter(e => e.category === 'Urgent').length;
  const actionEmails = activeEmails.filter(e => e.category === 'Action').length;

  // Apply top-level filter
  const filteredEmails = emailFilter === 'pending'
    ? activeEmails.filter(e => !completedEmailIds.includes(e.id))
    : emailFilter === 'done'
    ? activeEmails.filter(e => completedEmailIds.includes(e.id))
    : activeEmails;

  // Compute each trip's actual end date from its data (return date, or the latest
  // segment date/check-out) instead of trusting a manually-maintained "status" field,
  // which otherwise silently goes stale once a trip has actually happened.
  const tripEndDate = (trip) => {
    const dates = [trip.departureDate, trip.returnDate, ...(trip.segments || []).flatMap(s => [s.date, s.checkOut])].filter(Boolean);
    if (!dates.length) return null;
    return dates.reduce((max, d) => (new Date(d) > new Date(max) ? d : max));
  };
  const isTripUpcoming = (trip) => {
    const end = tripEndDate(trip);
    if (!end) return trip.status === 'upcoming';
    const endOfDay = new Date(end);
    endOfDay.setHours(23, 59, 59, 999);
    return endOfDay.getTime() >= Date.now();
  };
  const tripPhaseLabel = (trip) => {
    const start = trip.departureDate ? new Date(trip.departureDate) : null;
    return start && start.getTime() <= Date.now() ? 'Ongoing' : 'Upcoming';
  };
  const upcomingTrips = trips.filter(isTripUpcoming);
  const nextTrip = [...upcomingTrips].sort((a,b) => new Date(a.departureDate) - new Date(b.departureDate))[0];

  const formatTime = (iso) => {
    if (!iso) return '';
    try {
      if (iso.length === 10) return 'All day';
      return new Date(iso).toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
  };

  const formatDateWithTime = (iso) => {
    if (!iso) return '';
    try {
      const date = new Date(iso).toLocaleDateString('de-CH', { weekday: 'short', day: 'numeric', month: 'short' });
      if (iso.length === 10) return date;
      const time = new Date(iso).toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' });
      return `${date} · ${time}`;
    } catch { return iso; }
  };

  const formatTripDate = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleDateString('de-CH', { day: 'numeric', month: 'short' });
    } catch { return iso; }
  };

  const tabs = ['brief', 'travel', 'emails', 'finance', 'health', 'technology', 'insurance', 'foundation', 'life', 'reading', 'learning'];
  const tabLabels = { brief: "Today's Brief", technology: 'Technology & Risk', life: 'Life' };

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', fontFamily:'system-ui' }}>
      <div style={{ textAlign:'center', color: COLORS.subtext }}>
        <div style={{ fontSize:24, marginBottom:8 }}>⚡</div>
        <div>Loading Friedli Group...</div>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight:'100vh', background:'#FAF8F4' }}>
    <div style={{ maxWidth:980, margin:'0 auto', padding:'1.5rem 1rem 3rem', fontFamily:'system-ui, sans-serif', color: COLORS.text }}>

      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'1.5rem' }}>
        <div>
          <span style={{ fontSize:18, fontWeight:500 }}>Friedli<span style={{ color: COLORS.purple }}>Group</span></span>
          <span style={{ fontSize:13, color: COLORS.subtext, marginLeft:8 }}>/ CEO Dashboard</span>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <span style={{ fontSize:12, color: COLORS.subtext }}>{time.toLocaleTimeString('de-CH', { hour:'2-digit', minute:'2-digit' })}</span>
          <button
            onClick={loadData}
            style={{ fontSize:11, background:'#E1F5EE', color:'#0F6E56', padding:'3px 10px', borderRadius:20, fontWeight:500, border:'none', cursor:'pointer' }}
          >● Live</button>
        </div>
      </div>

      {/* Metric cards */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, marginBottom:16 }}>
        {[
          { label:'Emails today', value: activeEmails.length.toString(), sub:`${urgentEmails} urgent · ${actionEmails} action` },
          { label:'Priorities', value: staffPriorities.length.toString(), sub:'From Chief of Staff — needs your action', onClick: () => setActiveTab('brief') },
          { label:`API spend (${currentMonthLabel})`, value:`CHF ${apiSpend.current?.toFixed(2) || '0.00'}`, sub:`of CHF ${apiSpend.budget} budget` },
        ].map(c => (
          <div key={c.label} onClick={c.onClick} style={{ background: COLORS.lightGray, borderRadius:10, padding:'1rem', cursor: c.onClick ? 'pointer' : 'default' }}>
            <div style={{ fontSize:12, color: COLORS.subtext, marginBottom:4 }}>{c.label}</div>
            <div style={{ fontSize:22, fontWeight:500 }}>{c.value}</div>
            <div style={{ fontSize:12, color: COLORS.subtext, marginTop:2 }}>{c.sub}</div>
          </div>
        ))}
      </div>

      {/* Next trip banner */}
      {nextTrip && (
        <div style={{ background:'linear-gradient(135deg, #7F77DD18, #7F77DD08)', border:`1px solid ${COLORS.purple}30`, borderRadius:12, padding:'0.875rem 1.25rem', marginBottom:12, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <span style={{ fontSize:20 }}>✈️</span>
            <div>
              <div style={{ fontSize:13, fontWeight:500 }}>{nextTrip.destination}</div>
              <div style={{ fontSize:11, color: COLORS.subtext }}>{formatTripDate(nextTrip.departureDate)} → {formatTripDate(tripEndDate(nextTrip))}</div>
            </div>
          </div>
          <button onClick={() => setActiveTab('travel')} style={{ fontSize:11, color: COLORS.purple, background:'none', border:`1px solid ${COLORS.purple}40`, borderRadius:6, padding:'4px 10px', cursor:'pointer' }}>View trip →</button>
        </div>
      )}

      {/* Tabs — wraps onto multiple lines on narrow (mobile) screens instead of overflowing */}
      <div style={{ display:'flex', flexWrap:'wrap', gap:4, marginBottom:16, borderBottom:`1px solid ${COLORS.border}` }}>
        {tabs.map(tab => {
          const isActive = activeTab === tab;
          return (
          <button key={tab} onClick={() => setActiveTab(tab)} style={{
            padding:'8px 10px', border:'none', background:'none', cursor:'pointer',
            fontWeight: isActive ? 500 : 400,
            color: isActive ? COLORS.purple : COLORS.subtext,
            borderBottom: isActive ? `2px solid ${COLORS.purple}` : '2px solid transparent',
            fontSize:13, textTransform:'capitalize',
            whiteSpace:'normal', textAlign:'center', lineHeight:1.25,
            flex:'0 1 auto', minWidth:64, maxWidth:110,
          }}>
            {tabLabels[tab] || (tab.charAt(0).toUpperCase()+tab.slice(1))}
            {tab === 'tasks' && pendingTasks > 0 && <span style={{ marginLeft:6, background: COLORS.purple, color:'#fff', borderRadius:10, fontSize:10, padding:'1px 6px' }}>{pendingTasks}</span>}
            {tab === 'emails' && urgentEmails > 0 && <span style={{ marginLeft:6, background: COLORS.red, color:'#fff', borderRadius:10, fontSize:10, padding:'1px 6px' }}>{urgentEmails}</span>}
            {tab === 'travel' && upcomingTrips.length > 0 && <span style={{ marginLeft:6, background: COLORS.purple, color:'#fff', borderRadius:10, fontSize:10, padding:'1px 6px' }}>{upcomingTrips.length}</span>}
          </button>
          );
        })}
      </div>

      {/* TODAY'S BRIEF TAB */}
      {activeTab === 'brief' && (
        <div>
          {staffPriorities.length > 0 && (
            <div style={{ ...card, borderLeft:`3px solid ${COLORS.purple}` }}>
              {sectionHead('What needs your attention today', 'navy', '⚡', 12)}
              {staffPriorities.map((p, i) => (
                <div key={i} style={{ padding:'10px 0', borderBottom: i < staffPriorities.length-1 ? `0.5px solid ${COLORS.border}` : 'none' }}>
                  <div style={{ fontSize:14, fontWeight:500 }}>{p.title}</div>
                  {p.why && <div style={{ fontSize:12, color: COLORS.subtext, marginTop:3, lineHeight:1.5 }}>{p.why}</div>}
                  {p.recommendation && (
                    <div style={{ fontSize:12, color: COLORS.text, marginTop:5, background: COLORS.lightGray, borderRadius:6, padding:'6px 10px', display:'inline-block' }}>
                      💡 {p.recommendation}
                    </div>
                  )}
                </div>
              ))}
              {staffChiefData?.feedbackLoop && (
                <div style={{ fontSize:11, color: COLORS.subtext, marginTop:10, paddingTop:10, borderTop:`0.5px solid ${COLORS.border}`, lineHeight:1.6 }}>
                  💬 {staffChiefData.feedbackLoop}
                </div>
              )}
              {(staffChiefData?.resolvedRecently || []).length > 0 && (
                <div style={{ marginTop:10 }}>
                  {sectionHead('Recently cleared', 'teal', '✓', 4)}
                  {staffChiefData.resolvedRecently.map((r, i) => (
                    <div key={i} style={{ fontSize:12, color: COLORS.subtext, padding:'3px 0' }}>
                      <span style={{ textDecoration:'line-through' }}>{r.title}</span> — {r.how}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {brief.priorities && (
            <div style={{ ...card, borderLeft:`3px solid ${COLORS.purple}` }}>
              {sectionHead("Today's priorities", 'navy', '⚡', 10)}
              <p style={{ fontSize:14, lineHeight:1.7, margin:0 }}>{brief.priorities}</p>
            </div>
          )}
          <div style={grid(300)}>
            <div style={{ ...card, marginBottom:0 }}>
              {sectionHead('Today', 'slate', '📅', 10)}
              {(brief.calendar || []).length > 0
                ? brief.calendar.map((e,i) => (
                  <div key={i} style={{ display:'flex', gap:12, alignItems:'flex-start', padding:'7px 0', borderBottom: i < brief.calendar.length-1 ? `0.5px solid ${COLORS.border}` : 'none' }}>
                    <span style={{ fontSize:12, color: COLORS.subtext, minWidth:52, paddingTop:1 }}>
                      {formatTime(e.time)}
                    </span>
                    <span style={{ fontSize:13 }}>{e.title}</span>
                  </div>
                ))
                : <div style={{ fontSize:13, color: COLORS.subtext }}>No events today — free day ✓</div>
              }
            </div>
            {(brief.upcomingEvents || []).length > 0 && (
              <div style={{ ...card, marginBottom:0 }}>
                {sectionHead('Next 7 days', 'slate', '📆', 10)}
                {brief.upcomingEvents.map((e,i) => (
                  <div key={i} style={{ display:'flex', gap:10, alignItems:'flex-start', padding:'7px 0', borderBottom: i < brief.upcomingEvents.length-1 ? `0.5px solid ${COLORS.border}` : 'none' }}>
                    <span style={{ fontSize:12, color: COLORS.subtext, minWidth:110, paddingTop:1 }}>
                      {formatDateWithTime(e.time)}
                    </span>
                    <span style={{ fontSize:13 }}>{e.title}</span>
                  </div>
                ))}
              </div>
            )}
            <div style={{ ...card, marginBottom:0 }}>
              {sectionHead('Birthdays this week', 'rust', '🎂', 10)}
              {(brief.birthdaysThisWeek || []).length > 0 ? (
                brief.birthdaysThisWeek.map((b,i) => (
                  <div key={i} style={{ display:'flex', gap:10, alignItems:'flex-start', justifyContent:'space-between', padding:'7px 0', borderBottom: i < brief.birthdaysThisWeek.length-1 ? `0.5px solid ${COLORS.border}` : 'none' }}>
                    <span style={{ fontSize:13 }}>{b.name}</span>
                    <span style={{ fontSize:12, color: COLORS.subtext }}>{formatTripDateSafe(b.date)}</span>
                  </div>
                ))
              ) : (
                <div style={{ fontSize:13, color: COLORS.subtext }}>None this week.</div>
              )}
              {brief.nextBirthday && (
                <div style={{ fontSize:11, color: COLORS.subtext, marginTop: (brief.birthdaysThisWeek || []).length > 0 ? 8 : 4, paddingTop: (brief.birthdaysThisWeek || []).length > 0 ? 8 : 0, borderTop: (brief.birthdaysThisWeek || []).length > 0 ? `0.5px solid ${COLORS.border}` : 'none' }}>
                  ▶ Next up: {brief.nextBirthday.name} — {formatTripDateSafe(brief.nextBirthday.date)}
                </div>
              )}
            </div>
          </div>
          <div style={{ marginTop:16 }} />
          {brief.weather && (
            <div style={{ ...card, display:'flex', alignItems:'center', gap:16 }}>
              <div style={{ fontSize:32 }}>{brief.weather.icon || '🌤️'}</div>
              <div>
                <div style={{ fontSize:15, fontWeight:500 }}>{brief.weather.temp}°C · {brief.weather.condition}</div>
                <div style={{ fontSize:12, color: COLORS.subtext }}>Zürich · {brief.weather.note}</div>
              </div>
            </div>
          )}
          {lastFetch && <div style={{ fontSize:11, color:'#ccc', marginTop:8 }}>Last updated: {lastFetch.toLocaleTimeString('de-CH')}</div>}

          <div style={{ margin:'20px 0 10px' }}>{sectionHead('Chief updates', 'slate')}</div>
          <ChiefSection chief={findChief('Chief of Staff')} heading="Chief of Staff" />
        </div>
      )}

      {/* TASKS TAB */}
      {activeTab === 'tasks' && (
        <div>
          <div style={{ ...card, display:'flex', gap:8, alignItems:'center' }}>
            <input value={newTaskText} onChange={e => setNewTaskText(e.target.value)} onKeyDown={e => e.key==='Enter' && addTask()} placeholder="Add a new task..." style={{ flex:1, padding:'8px 12px', borderRadius:8, border:`1px solid ${COLORS.border}`, fontSize:13, outline:'none' }} />
            <select value={newTaskPriority} onChange={e => setNewTaskPriority(e.target.value)} style={{ padding:'8px 10px', borderRadius:8, border:`1px solid ${COLORS.border}`, fontSize:12, background:'#fff' }}>
              <option value="high">🔴 High</option>
              <option value="medium">🟡 Medium</option>
              <option value="low">⚪ Low</option>
            </select>
            <button onClick={addTask} disabled={taskSaving || !newTaskText.trim()} style={{ padding:'8px 16px', background: COLORS.purple, color:'#fff', border:'none', borderRadius:8, fontSize:13, cursor:'pointer', fontWeight:500, opacity: taskSaving ? 0.6 : 1 }}>
              {taskSaving ? '...' : '+ Add'}
            </button>
          </div>
          <div style={card}>
            {tasks.filter(t => !t.done).length === 0 && tasks.filter(t => t.done).length === 0 && <div style={{ fontSize:13, color: COLORS.subtext, textAlign:'center', padding:'1rem' }}>No tasks yet — add one above!</div>}
            {tasks.filter(t => !t.done).map((task,i,arr) => (
              <div key={task.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 0', borderBottom: i < arr.length-1 ? `0.5px solid ${COLORS.border}` : 'none' }}>
                <button onClick={() => toggleTask(task.id)} style={{ width:18, height:18, borderRadius:4, border:`1.5px solid ${COLORS.border}`, background:'#fff', cursor:'pointer', flexShrink:0 }} />
                <span style={{ flex:1, fontSize:13 }}>{task.text}</span>
                <span style={pill(priorityColor(task.priority))}>{task.priority}</span>
                <button onClick={() => deleteTask(task.id)} style={{ background:'none', border:'none', cursor:'pointer', color: COLORS.subtext, fontSize:16, padding:'0 4px' }}>×</button>
              </div>
            ))}
            {tasks.filter(t => t.done).length > 0 && (
              <div style={{ marginTop:12 }}>
                <div style={{ fontSize:11, color: COLORS.subtext, marginBottom:8 }}>COMPLETED</div>
                {tasks.filter(t => t.done).map((task,i,arr) => (
                  <div key={task.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 0', borderBottom: i < arr.length-1 ? `0.5px solid ${COLORS.border}` : 'none', opacity:0.5 }}>
                    <button onClick={() => toggleTask(task.id)} style={{ width:18, height:18, borderRadius:4, border:'none', background: COLORS.green, cursor:'pointer', flexShrink:0, color:'#fff', fontSize:11 }}>✓</button>
                    <span style={{ flex:1, fontSize:13, textDecoration:'line-through' }}>{task.text}</span>
                    <button onClick={() => deleteTask(task.id)} style={{ background:'none', border:'none', cursor:'pointer', color: COLORS.subtext, fontSize:16, padding:'0 4px' }}>×</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* TRAVEL TAB */}
      {activeTab === 'travel' && (
        <div>
          {upcomingTrips.length === 0
            ? <div style={card}><div style={{ fontSize:13, color: COLORS.subtext }}>No upcoming trips — enjoy the downtime! ✓</div></div>
            : [...upcomingTrips].sort((a,b) => new Date(a.departureDate) - new Date(b.departureDate)).map((trip, ti) => (
              <div key={ti} style={{ ...card, marginBottom:16 }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
                  <div>
                    <div style={{ fontSize:16, fontWeight:600 }}>✈️ {trip.destination}</div>
                    <div style={{ fontSize:12, color: COLORS.subtext, marginTop:2 }}>{formatTripDate(trip.departureDate)} → {formatTripDate(tripEndDate(trip))}</div>
                  </div>
                  <span style={pill(COLORS.green)}>{tripPhaseLabel(trip)}</span>
                </div>
                {trip.note && (
                  <div style={{ fontSize:12, color: COLORS.subtext, fontStyle:'italic', marginBottom:12 }}>{trip.note}</div>
                )}
                {(trip.segments || []).length > 0 && sectionHead('Itinerary', 'navy', null, 10)}
                {(trip.segments || []).map((seg, si) => (
                  <div key={si} style={{ display:'flex', gap:12, alignItems:'flex-start', padding:'10px 0', borderBottom: si < trip.segments.length-1 ? `0.5px solid ${COLORS.border}` : 'none' }}>
                    <span style={{ fontSize:18, minWidth:28 }}>{segmentIcon(seg.type)}</span>
                    <div style={{ flex:1, display:'flex', gap:12, flexWrap:'wrap', alignItems:'flex-start' }}>
                      <div style={{ flex:'1 1 220px', minWidth:200 }}>
                        <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:8 }}>
                          <div style={{ fontSize:13, fontWeight:500 }}>{seg.details}</div>
                          {seg.confirmed && <span style={pill(COLORS.green)}>Confirmed</span>}
                        </div>
                        <div style={{ fontSize:12, color: COLORS.subtext, marginTop:2 }}>
                          {formatTripDate(seg.date)}
                          {seg.time && ` · ${seg.time}`}
                          {seg.checkOut && ` → ${formatTripDate(seg.checkOut)}`}
                          {seg.bookingRef && ` · Ref: ${seg.bookingRef}`}
                        </div>
                        {seg.type === 'accommodation' && (seg.roomType || seg.guests || seg.rooms || seg.totalPriceCHF) && (
                          <div style={{ fontSize:12, color: COLORS.text, marginTop:6, display:'flex', flexWrap:'wrap', gap:6 }}>
                            {seg.roomType && <span style={{ background: COLORS.lightGray, borderRadius:20, padding:'3px 10px' }}>{seg.roomType}</span>}
                            {seg.rooms && <span style={{ background: COLORS.lightGray, borderRadius:20, padding:'3px 10px' }}>{seg.rooms} room{seg.rooms !== 1 ? 's' : ''}</span>}
                            {seg.guests && <span style={{ background: COLORS.lightGray, borderRadius:20, padding:'3px 10px' }}>{seg.guests} guest{seg.guests !== 1 ? 's' : ''}</span>}
                            {seg.totalPriceCHF != null && (
                              <span style={{ background: COLORS.lightGray, borderRadius:20, padding:'3px 10px' }}>
                                {formatCHF(seg.totalPriceCHF)} total{seg.pricePerNightCHF ? ` · ${formatCHF(seg.pricePerNightCHF)}/night` : (seg.pricePerRoomPerNightCHF ? ` · ${formatCHF(seg.pricePerRoomPerNightCHF)}/room/night` : '')}
                              </span>
                            )}
                            {seg.totalPriceTHB != null && <span style={{ background: COLORS.lightGray, borderRadius:20, padding:'3px 10px' }}>THB {formatNum(seg.totalPriceTHB)}</span>}
                          </div>
                        )}
                        {seg.note && (
                          <div style={{ fontSize:12, color: COLORS.subtext, marginTop:6, fontStyle:'italic' }}>{seg.note}</div>
                        )}
                      </div>
                      {seg.type === 'accommodation' && (
                        <div style={{ flex:'0 0 150px', width:150, borderRadius:8, overflow:'hidden', border:`0.5px solid ${COLORS.border}` }}>
                          <iframe
                            title={`Map of ${seg.details}`}
                            width="150" height="100" style={{ border:0, display:'block' }} loading="lazy"
                            src={`https://www.google.com/maps?q=${encodeURIComponent(seg.details + ', ' + trip.destination)}&output=embed`}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ))
          }
        </div>
      )}

      {/* EMAILS TAB */}
      {activeTab === 'emails' && (
        <div>
          <ChiefSection chief={findChief('Chief of Communication')} heading="Chief of Communication" />
          <ChiefSection chief={findChief('Chief of Security')} heading="Chief of Security" />

          {/* Toolbar */}
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
            <div style={{ fontSize:12, color: COLORS.subtext }}>
              Last updated: {lastFetch ? lastFetch.toLocaleTimeString('de-CH', { hour:'2-digit', minute:'2-digit' }) : '—'}
            </div>
            <div style={{ display:'flex', gap:6 }}>
              {['all', 'pending', 'done'].map(f => (
                <button
                  key={f}
                  onClick={() => setEmailFilter(f)}
                  style={{
                    fontSize: 11, padding: '4px 12px', borderRadius: 20, cursor: 'pointer',
                    border: `0.5px solid ${emailFilter === f ? COLORS.text : COLORS.border}`,
                    background: emailFilter === f ? COLORS.text : '#fff',
                    color: emailFilter === f ? '#fff' : COLORS.subtext,
                    fontWeight: emailFilter === f ? 500 : 400,
                    textTransform: 'capitalize',
                  }}
                >{f}</button>
              ))}
            </div>
          </div>

          {/* Buckets */}
          {activeEmails.length === 0 ? (
            <div style={{ ...card, fontSize:13, color: COLORS.subtext }}>No emails processed yet today.</div>
          ) : (
            BUCKET_CONFIG.map(bucket => (
              <EmailBucket
                key={bucket.key}
                bucket={bucket}
                emails={filteredEmails.filter(e => matchBucket(e, bucket.key))}
                completedEmailIds={completedEmailIds}
                expandedEmailId={expandedEmailId}
                onToggleExpand={toggleEmailExpand}
                onToggleComplete={toggleEmailComplete}
              />
            ))
          )}
        </div>
      )}

      {/* FINANCE TAB */}
      {activeTab === 'finance' && (
        <FinanceSection chief={findChief('Chief of Finance')} />
      )}

      {/* HEALTH TAB */}
      {activeTab === 'health' && (
        <HealthSection chief={findChief('Chief of Health')} />
      )}

      {/* TECHNOLOGY & RISK TAB (combined per Simon's request) */}
      {activeTab === 'technology' && (
        <TechRiskSection tech={findChief('Chief of Technology')} risk={findChief('Chief of Risk')} />
      )}

      {/* INSURANCE TAB */}
      {activeTab === 'insurance' && (
        <InsuranceSection chief={findChief('Chief of Insurance')} />
      )}

      {/* FOUNDATION TAB */}
      {activeTab === 'foundation' && (
        <FoundationSection chief={findChief('Chief of Foundation')} />
      )}

      {/* LIFE TAB */}
      {activeTab === 'life' && (
        <ChiefOfLifeSection chief={findChief('Chief of Life')} />
      )}

      {/* READING TAB */}
      {activeTab === 'reading' && (
        <ReadingSection chief={findChief('Chief of Reading')} />
      )}

      {/* LEARNING TAB */}
      {activeTab === 'learning' && (
        <LearningSection chief={findChief('Chief of Learning')} />
      )}

    </div>
    </div>
  );
}